import { computed } from "vue";
import { useStoreItem } from "../itemStore.store";

import { useModelStore } from "../model/model.store";
import { splitUid } from "@/helpers/utility/Id";
import { useSceneNodesStore, sceneNodeItemStructure } from "../scene/sceneNodes.store";
import { isSceneTextNode } from "./sceneTextFunctions";
import type { SceneTextData } from "./sceneTextData";

export const useSceneText = useStoreItem(sceneNodeItemStructure, useSceneNodesStore, (nodeRef, nodeStore) => {
  const modelStore = useModelStore();

  const textValue = computed({
    get() {
      const node = nodeRef.value;
      if (!node) return "";
      if (!isSceneTextNode(node)) return "";
      const data = node.data;
      if (!data.connected || !data.connectedUid || !data.connectedProperty) {
        return data.text;
      }
      const { type, id } = splitUid(data.connectedUid);
      const item = modelStore.getItem(type, id);
      if (!item) return data.text;
      const connectedProperty = data.connectedProperty as keyof typeof item;
      return item[connectedProperty] as string;
    },
    set(value: string) {
      const node = nodeRef.value;
      if (!node) return;
      if (!isSceneTextNode(node)) return "";
      const data = node.data;
      if (data.connected && data.connectedUid && data.connectedProperty) {
        const { type, id } = splitUid(data.connectedUid);
        const connectedItem = modelStore.getItem(type, id);
        if (connectedItem) {
          type T = typeof connectedItem;
          const connectedProperty = data.connectedProperty as keyof T;
          if (connectedItem[connectedProperty] == value) return;
          const store = modelStore.getStore(type);
          if (store) {
            const updateKey = store.updateKey as (id: number, key: keyof T, value: string) => void;
            updateKey(connectedItem.id, connectedProperty, value);
            return;
          }
        }
      }

      nodeStore.updateDataKey<"text">(node.id, "text", value);
    }
  });

  const data = computed(() => {
    const node = nodeRef.value;
    if (!node) return null;
    if (!isSceneTextNode(node)) return null;
    return node.data;
  });

  function updateData(newData: Partial<SceneTextData>) {
    const node = nodeRef.value;
    if (!node) return;
    if (!isSceneTextNode(node)) return;
    nodeStore.updateData<"text">(node.id, newData);
  }

  return {
    textValue,
    data,
    updateData
  };
});
